import React, { useEffect, useState } from 'react';
import { ConfirmationModalProps } from '~/Components';
import { IMenuAction } from '~/Models/interfaces/menuAction.models';
import { Trans, useTranslation } from 'react-i18next';
import { useProvider, useNavigation } from '~/Hooks';
import { Typography } from '~/UIKit';
import { makeStyles } from '~/UIKit/Style';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { RED_USC } from '~/Constants/Colors.constants';
import { useRequestDeleteVOD } from '~/requests/videoOnDemandService/useRequestDeleteVOD';
import { VoDPreview } from './VideoOnDemandEdit/VideoOnDemandPreview';
import { Route } from '~/Constants/Routes.constants';
import { routeReplacer } from '~/Helpers/String.helper';

export const useStyles = makeStyles(() => ({
  confirmationParagraph: {
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      color: `${RED_USC}`,
      marginRight: '5px',
      width: '20px',
    },
  },
  previewContent: {
    height: 320,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

const vodModalsMessages = {
  delete: {
    title: 'app.vod.modals.delete_video.title',
    topcontent: 'app.vod.modals.delete_video.topcontent',
    bottomcontent: 'app.vod.modals.delete_video.bottomcontent',
    cancel: 'app.vod.modals.delete_video.cancel',
    confirm: 'app.vod.modals.delete_video.confirm',
  },
} as any;

const initalState = {
  open: false,
  cancel: {},
  confirm: {},
  title: '',
  destructive: false,
};
export const playableStatus = ['Scheduled', 'Published', 'Unpublished'];

export const useVODModals = () => {
  const { navigate } = useNavigation();
  const { classes } = useStyles();
  const [type, setType] = useState<string>('');
  const [videoId, setVideoId] = useState<string>('');
  const [confirmationModalProps, setConfirmationModalProps] = useState<ConfirmationModalProps>(
    initalState,
  );
  const { currentProvider } = useProvider();
  const { t } = useTranslation();
  const { mutate: deleteVOD } = useRequestDeleteVOD();

  const actions: (item: any) => IMenuAction[] = (item) => {
    const items: IMenuAction[] = [
      {
        name: t('app.preview'),
        onClick: () => {
          setVideoId(item?.id);
          setType('preview');
        },
        hide: !playableStatus.includes(item?.status),
      },
      {
        name: t('app.vod.menu.edit'),
        onClick: () => {
          const url = routeReplacer(Route.VIDEO_ON_DEMAND_EDIT, {
            domain: currentProvider?.domain || '',
            id: item?.id,
          });
          navigate?.(url);
        },
      },
      {
        name: t('app.vod.menu.delete'),
        onClick: () => {
          setVideoId(item?.id);
          setType('delete');
        },
      },
    ];

    return items.filter((obj) => !obj.hide);
  };

  useEffect(() => {
    if (!type?.length) {
      setConfirmationModalProps(initalState);
      return;
    }
    if (type === 'preview') {
      setConfirmationModalProps({
        title: '',
        open: true,
        destructive: ['preview'].includes(type),
        confirm: {
          hidden: true,
        },
        cancel: {
          action: () => setType(''),
          title: t('app.close'),
        },
        children: (
          <div className={classes.previewContent}>
            <VoDPreview videoId={videoId} />
          </div>
        ),
        rawContent: true,
      });
    } else {
      setConfirmationModalProps({
        title: t(vodModalsMessages[type].title),
        open: true,
        destructive: ['delete'].includes(type),
        confirm: {
          action: () => {
            if (type === 'delete') {
              deleteVOD(videoId);
              setType('');
            }
          },
          title: t(vodModalsMessages[type].confirm),
        },
        cancel: {
          action: () => setType(''),
          title: t(vodModalsMessages[type].cancel),
        },
        children: (
          <div>
            <Typography>
              <Trans
                i18nKey={vodModalsMessages[type].topcontent}
                values={{ name: currentProvider?.name }}
                components={[<b key="bold" />]}
              />
            </Typography>
            <Typography className={classes.confirmationParagraph} variant="body1">
              <WarningAmberIcon />
              {t(vodModalsMessages[type].bottomcontent)}
            </Typography>
          </div>
        ),
      });
    }
  }, [type]);

  return {
    confirmationModalProps,
    actions,
  };
};
